import type { Msg } from '../i18n'

// Пустая подложка обязана называть себя.
//
// Яндекс на плохой или протухший ключ отвечает 403 (`Invalid api key`), без
// ключа — 400, и тайла не отдаёт НИ ОДНОГО (проба 27.08.2026). Это не то же,
// что у Carto: тот при отказе штампует «API KEY REQUIRED», но карту рисует.
// У Яндекса MapLibre молча оставит фон, наши поля повиснут в пустоте — и
// выглядит это как сломанная карта, а не как истёкший ключ.
//
// Автоматического отката на Carto здесь НЕТ (решение владельца 27.08.2026 —
// «лишняя движущаяся часть»), поэтому единственное, чем мы располагаем, —
// сказать вслух.

/** Сколько отказов плиток подряд считаем отказом подложки.
 *
 *  Одна ошибка — не отказ: плитка теряется и на живой сети. Порог заведомо
 *  меньше видимого кадра (десятки плиток), иначе на мелком зуме, где плиток
 *  единицы, полоска не появилась бы вовсе — то есть отказ снова стал бы немым
 *  ровно там, где смотреть на пустую карту дольше всего. */
export const BASEMAP_FAIL_LIMIT = 4

export function basemapDown(failures: number): boolean {
  return failures >= BASEMAP_FAIL_LIMIT
}

/** Род отказа подложки — по нему видно, КОМУ чинить.
 *
 *  Статус HTTP значит, что поставщик ответил, и беда у него или в нашем ключе;
 *  его отсутствие — что запрос до поставщика не дошёл: его срезал блокировщик
 *  рекламы, VPN или сеть человека. Бой 17.10.2026: в одном Chrome подложки не
 *  было при живом ключе, и общая фраза «не отвечает» уводила разбор на сервер. */
export type BasemapFailure =
  | { kind: 'rejected' | 'limit' | 'server' | 'other'; status: number }
  | { kind: 'blocked' }

/** Разбирает ошибку плитки из события MapLibre (`e.error`). `null` — не отказ.
 *
 *  Статус читается полем, а не через `instanceof AJAXError`: ошибка может
 *  прийти из воркера, где класс не переживает пересылку. */
export function basemapFailure(error: unknown): BasemapFailure | null {
  const e = error as { name?: unknown; status?: unknown } | null | undefined
  if (e?.name === 'AbortError') return null
  const status = typeof e?.status === 'number' ? e.status : 0
  if (status <= 0) return { kind: 'blocked' }
  if (status === 400 || status === 401 || status === 403) return { kind: 'rejected', status }
  if (status === 429) return { kind: 'limit', status }
  if (status >= 500) return { kind: 'server', status }
  return { kind: 'other', status }
}

const FAILURE_KEY: Record<BasemapFailure['kind'], string> = {
  rejected: 'map.basemap.rejected',
  limit: 'map.basemap.limit',
  server: 'map.basemap.server',
  other: 'map.basemap.http',
  blocked: 'map.basemap.blocked',
}

/** Ключ каталога надписи; без известной причины — прежняя общая фраза. */
export function basemapFailureMsg(f: BasemapFailure | null): Msg {
  if (!f) return { key: 'map.basemap.down' }
  return 'status' in f
    ? { key: FAILURE_KEY[f.kind], vars: { status: f.status } }
    : { key: FAILURE_KEY[f.kind] }
}
